import ColorsStyles from '@Styles/colors.scss';
import { useAuth0 } from '@auth0/auth0-react';
import _isNil from 'lodash/isNil';
import React, { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import FolderStyles from '@Components/FolderComponents/Folders.scss';
import PopoverMenu from '@Components/PopoverMenu/PopoverMenu';
import PopoverMenuItem from '@Components/PopoverMenu/PopoverMenuItem';
import { IFolder } from '@Data/folders/Interfaces';
import {
    selectedFolderIdsState,
    visibleFoldersState,
} from '@Data/folders/Reducer';
import ProcessesApi from '@Data/processes/Api';
import { tryCreateProcessNotificationForTask } from '@Data/processes/Helpers';
import { IProcess, IRunningProcess } from '@Data/processes/Interfaces';
import {
    iiqAutomationTaskState,
    linkDepartmentProcessState,
} from '@Data/processes/Reducer';
import { TaskType } from '@Data/processes/TaskType';
import ProjectsApi from '@Data/projects/Api';
import { selectedProjectState } from '@Data/projects/Reducer';
import { selectedTeamState } from '@Data/teams/Reducer';
import { getParsedAuth0Username } from '@Services/authentication/Helpers';

interface IQcFolderMenuProps {
    folder: any;
    onViewFiles: (folder: any) => void;
    onJobSettingsClick: (folder: any) => void;
    onSetStatus: (folder: any) => void;
    selectedFolder?: any;
    onProcess: (folder: any) => void;
    onShare: (folder: any) => void;
    onAutoQc: (folder: any, iiqAutomationTask: IProcess) => void;
    onCreateSubset: (folder: any) => void;
    id: string;
}

export const QcFolderMenu = (props: IQcFolderMenuProps) => {
    const {
        folder,
        onViewFiles,
        onJobSettingsClick,
        selectedFolder,
        id,
        onProcess,
        onShare,
        onAutoQc,
        onCreateSubset,
    } = props;

    const selectedProject = useSelector(selectedProjectState);
    const linkDepartmentProcess = useSelector(linkDepartmentProcessState);
    const iiqAutomationTask = useSelector(iiqAutomationTaskState);
    const selectedTeam = useSelector(selectedTeamState);
    const menuRef = useRef<PopoverMenu>();
    const [btnColour, setBtnColour] = useState('#000');
    const { user } = useAuth0();
    let syncDeltaString = '';
    const lastIoSyncTime: string = folder?.last_io_sync_time;
    if (!_isNil(lastIoSyncTime)) {
        const syncDelta = Date.now() - Date.parse(lastIoSyncTime);
        syncDeltaString = `${Math.floor(syncDelta / 60000)}m ago`;
    }

    const [isSelected, setIsSelected] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    const visibleFolders: Array<IFolder> = useSelector(visibleFoldersState);
    const selectedFolderIds: Array<string> = useSelector(
        selectedFolderIdsState
    );

    useMemo(() => {
        if (selectedFolderIds.includes(folder.folder_id)) {
            setIsSelected(true);
        }
        if (visibleFolders.map((f) => f.folder_id).includes(folder.folder_id)) {
            setIsVisible(true);
        }
    }, [folder, visibleFolders, selectedFolderIds]);

    const handleOpenIkeOfficeLink = (selectedFolder) => {
        const departmentId = selectedProject?.department_id;
        const jobId = selectedFolder?.job_id;
        const ikeOfficeEntityLink = `https://office.ikegps.com/#/measure?d=${departmentId}&t=${jobId}`;
        window.open(ikeOfficeEntityLink, '_blank');
    };

    const handleSetColours = (isOpen: boolean) => {
        if (isOpen) {
            setBtnColour(ColorsStyles.ikeGradient8);
        } else {
            setBtnColour(ColorsStyles.ikeGradient1);
        }
    };

    const handleClose = () => {
        handleSetColours(false);
    };

    const handleFolderSync = async (rebuildPolesOnly = false) => {
        const processSchema = {
            departmentId: selectedProject?.department_id,
            departmentName: selectedProject?.department_name,
            jobId: folder?.job_id,
        };

        if (rebuildPolesOnly) {
            processSchema['rebuild_poles_only'] = 1;
        }

        await ProcessesApi.runProcess(
            linkDepartmentProcess,
            processSchema,
            selectedProject.project_id,
            getParsedAuth0Username(user.sub),
            (runningProcess: IRunningProcess) => {
                tryCreateProcessNotificationForTask(
                    runningProcess.process_name,
                    runningProcess.task_id,
                    TaskType.SYNC
                );
            }
        );
        await ProjectsApi.getAndSelectProjectById(
            selectedTeam.team_id,
            selectedProject.project_id
        );
        ProcessesApi.setProcess(null);
        handleClose();
    };

    return (
        <div className={FolderStyles.foMenuContainer}>
            <PopoverMenu
                id={id}
                ref={menuRef}
                iconColour={btnColour}
                onClose={handleClose}
                onOpen={() => handleSetColours(true)}
            >
                <PopoverMenuItem
                    id={`${id}jobsettings`}
                    text="Job Settings"
                    parentRef={menuRef}
                    onClick={() => {
                        onJobSettingsClick(selectedFolder);
                    }}
                />
                {folder?.folder_type === 'io_import_v2' && (
                    <>
                        <PopoverMenuItem
                            id={`${id}syncfolder`}
                            onClick={() => handleFolderSync()}
                            text={`Sync folder ${syncDeltaString}`}
                            parentRef={menuRef}
                        />
                        <PopoverMenuItem
                            id={`${id}syncPoles`}
                            onClick={() => handleFolderSync(true)}
                            text={'Rebuild Poles'}
                            parentRef={menuRef}
                        />
                    </>
                )}
                <PopoverMenuItem
                    id={`${id}-auto-qc`}
                    onClick={() => onAutoQc(selectedFolder, iiqAutomationTask)}
                    text="Auto QC Checks"
                    parentRef={menuRef}
                />
                <PopoverMenuItem
                    id={`${id}files`}
                    onClick={() => onViewFiles(selectedFolder)}
                    text="files"
                    parentRef={menuRef}
                />
                <PopoverMenuItem
                    id={`${id}setstatus`}
                    text="Open job in IKE Office"
                    parentRef={menuRef}
                    onClick={() => handleOpenIkeOfficeLink(selectedFolder)}
                />
                <PopoverMenuItem
                    id={`${id}-process`}
                    onClick={() => onProcess(selectedFolder)}
                    text="process"
                    parentRef={menuRef}
                />
                <PopoverMenuItem
                    id={`${id}-share`}
                    onClick={() => onShare(selectedFolder)}
                    text="share"
                    parentRef={menuRef}
                />
                {/*
                <PopoverMenuItem
                    id={`${id}-create-subset`}
                    onClick={onCreateSubset}
                    text="create subset"
                    parentRef={menuRef}
                />
                */}
            </PopoverMenu>
        </div>
    );
};
